import type { CreateAdminUserDto, UpdateAdminUserDto } from '@idc-forms/api-client'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import useAuth from '@/features/auth/hooks/use-auth'
import { queryKeys } from '@/shared/constants/query-keys'
import { getErrorMessage } from '@/shared/utils/handle-api-error'
import { usersAdminV2Service } from '../services/users-admin-v2.service'

/**
 * Mutaciones de usuarios contra la API v2. Identifican por id público (`usr_`),
 * llevan el Bearer de la sesión staff v2 y, al completar, invalidan todo el
 * árbol `['users']` (listado v2 + summary v2). El create no pide contraseña: el
 * backend genera una aleatoria y la envía por correo.
 */
function useV2AccessToken() {
  const { auth } = useAuth()
  return auth?.v2AccessToken ?? ''
}

export const useCreateUserV2Mutation = () => {
  const queryClient = useQueryClient()
  const accessToken = useV2AccessToken()
  return useMutation({
    mutationFn: async (body: CreateAdminUserDto) =>
      await usersAdminV2Service.create(accessToken, body),
    onSuccess: () => {
      toast.success('Usuario creado — la contraseña inicial se envió a su correo')
      void queryClient.invalidateQueries({ queryKey: queryKeys.users.all })
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    }
  })
}

export const useUpdateUserV2Mutation = () => {
  const queryClient = useQueryClient()
  const accessToken = useV2AccessToken()
  return useMutation({
    mutationFn: async ({ id, body }: { id: string; body: UpdateAdminUserDto }) =>
      await usersAdminV2Service.update(accessToken, id, body),
    onSuccess: () => {
      toast.success('Usuario actualizado')
      void queryClient.invalidateQueries({ queryKey: queryKeys.users.all })
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    }
  })
}
